import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../store'
import { CustomDefinition, CustomMetricSource, CustomMetricValueType } from '../../../interfaces/db'
import { getServicesManager } from '../../../services'
import { isDefinitionValid } from '../utils'
import { MetricTemplate } from '../types'
import { getCustomCode } from '../cfg'
import { CustomCode } from '../cfg'
import {
  openEditModal,
  closeEditModal,
  openTestModal,
  closeTestModal,
  switchFromEditToTest,
  switchFromTestToEdit,
  setTemplateTab,
  setHasUnsavedChanges,
  setError,
  clearError,
  setSnackbarOpen,
  CustomScoresView
} from '../../../lib/redux/custom-scores/slice'

export const useCustomScores = () => {
  const dispatch = useDispatch()
  const team_id = useSelector((state: RootState) => state.user.value?.team_id ?? '')
  
  // Redux state
  const customScoresState = useSelector((state: RootState) => state.customScores)
  const {
    currentView,
    editingDefinition,
    testingMetric,
    templateTab,
    hasUnsavedChanges,
    error,
    snackbarOpen
  } = customScoresState
  
  // Local data state (could be moved to Redux too if needed)
  const [definitions, setDefinitions] = useState<CustomDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [metricChanges, setMetricChanges] = useState<any[]>([])
  const [initialDefinitions, setInitialDefinitions] = useState<CustomDefinition[]>([])

  // Computed values
  const codeOptions: CustomCode[] = getCustomCode(team_id) ?? []

  // Load data on mount
  useEffect(() => {
    loadData()
  }, [team_id])

  // Update Redux when definitions change
  useEffect(() => {
    const hasChanges = JSON.stringify(definitions) !== JSON.stringify(initialDefinitions)
    dispatch(setHasUnsavedChanges(hasChanges))
  }, [definitions, initialDefinitions, dispatch])

  // Beforeunload listener for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const loadData = async () => {
    try {
      setLoading(true)
      const services = getServicesManager()
      const data = await services.getCustomDefinitions()
      
      if (Array.isArray(data)) {
        setDefinitions(data)
        setInitialDefinitions(data)
      }
    } catch (err) {
      console.error('Failed to load custom score definitions:', err)
      dispatch(setError('Failed to load custom scores'))
    } finally {
      setLoading(false)
    }
  }

  const handleSave = useCallback(async (definitionsToSave?: CustomDefinition[]) => {
    const defsToSave = definitionsToSave || definitions
    
    try {
      const services = getServicesManager()
      const mockResult = await services.putCustomDefinitions(defsToSave, true)

      if (typeof mockResult === 'string') {
        dispatch(setError(mockResult))
        return
      }

      if (!mockResult) {
        dispatch(setError('Failed to validate changes'))
        return
      }
      
      // Check for potentially breaking changes
      const changes: any[] = []
      defsToSave.forEach((def, index) => {
        const original = initialDefinitions.find(orig => orig.custom_definition_id === def.custom_definition_id)
        if (original) {
          const hasBreakingChanges = 
            original.value_source !== def.value_source ||
            original.value_type !== def.value_type ||
            original.value_type_enum_str !== def.value_type_enum_str
          
          if (hasBreakingChanges) {
            changes.push({
              metric: def.label,
              changes: [
                original.value_source !== def.value_source && 'Source changed',
                original.value_type !== def.value_type && 'Output type changed',
                original.value_type_enum_str !== def.value_type_enum_str && 'Enum values changed'
              ].filter(Boolean)
            })
          }
        }
      })

      if (changes.length > 0) {
        setMetricChanges(changes)
        setShowConfirmDialog(true)
        return
      }

      await confirmSave(defsToSave)
    } catch (err) {
      dispatch(setError('Failed to validate changes'))
    }
  }, [definitions, initialDefinitions, dispatch])

  const confirmSave = useCallback(async (defsToSave?: CustomDefinition[]) => {
    const definitionsToSave = defsToSave || definitions
    try {
      const services = getServicesManager()
      
      // Validate all definitions
      const invalidDefs = definitionsToSave.filter(def => !isDefinitionValid(def).valid)
      if (invalidDefs.length > 0) {
        dispatch(setError('Cannot save: Some metrics are invalid'))
        return
      }

      const result = await services.putCustomDefinitions(definitionsToSave)
      if (typeof result === 'string') {
        dispatch(setError(result))
        return
      }
      
      setInitialDefinitions([...definitionsToSave])
      dispatch(setError('Custom scores saved successfully!'))
    } catch (err) {
      console.error('Failed to save custom score definitions:', err)
      dispatch(setError('Failed to save custom scores'))
    } finally {
      setShowConfirmDialog(false)
    }
  }, [definitions, dispatch])

  const addNewDefinition = useCallback((template?: Partial<CustomDefinition> | MetricTemplate) => {
    const newDef: CustomDefinition = {
      custom_definition_id: `temp_${Date.now()}`,
      team_id: team_id,
      label: template?.label || '',
      description: template?.description || '',
      order: null,
      tags: null,
      value_source: template?.value_source || CustomMetricSource.GEN_AI,
      value_type: template?.value_type || CustomMetricValueType.BOOLEAN,
      value_type_enum_str: template?.value_type_enum_str || null,
      value_gen_str: template?.value_gen_str || null,
      value_gen_desc_str: template?.value_gen_desc_str || null,
      value_phrase_str: template?.value_phrase_str || null,
      value_phrase_role: template?.value_phrase_role || null,
      value_average_str: template?.value_average_str || null,
      value_code_str: template?.value_code_str || null,
      where_min_duration: template?.where_min_duration || null,
      where_max_duration: template?.where_max_duration || null,
      where_gen_str: null,
      modified_as_of: null,
      rebuilt_as_of: null,
      metric_idx: null
    }
    dispatch(openEditModal(newDef))
  }, [team_id, dispatch])

  const handleDelete = useCallback((def: CustomDefinition) => {
    setDefinitions(prev => prev.filter(d => d.custom_definition_id !== def.custom_definition_id))
  }, [])

  const handleEditSave = useCallback((updatedDef: CustomDefinition) => {
    setDefinitions(prev => {
      const existing = prev.find(d => d.custom_definition_id === updatedDef.custom_definition_id)
      if (existing) {
        return prev.map(d => d.custom_definition_id === updatedDef.custom_definition_id ? updatedDef : d)
      } else {
        return [...prev, updatedDef]
      }
    })
    dispatch(closeEditModal())
  }, [dispatch])

  const handleUndoChanges = useCallback(() => {
    setDefinitions([...initialDefinitions])
  }, [initialDefinitions])

  const handleCloseConfirmDialog = useCallback(() => {
    setShowConfirmDialog(false)
    setMetricChanges([])
  }, [])

  // Redux action dispatchers
  const handleTestFromEdit = useCallback((metric: CustomDefinition) => {
    // Save the metric first
    setDefinitions(prev => {
      const existing = prev.find(d => d.custom_definition_id === metric.custom_definition_id)
      if (existing) {
        return prev.map(d => d.custom_definition_id === metric.custom_definition_id ? metric : d)
      } else {
        return [...prev, metric]
      }
    })
    
    dispatch(switchFromEditToTest(metric))
  }, [dispatch])

  const handleEditFromTest = useCallback((metric: CustomDefinition) => {
    dispatch(switchFromTestToEdit(metric))
  }, [dispatch])

  return {
    // State
    definitions,
    loading,
    currentView,
    editingDefinition,
    testingMetric,
    showConfirmDialog,
    metricChanges,
    templateTab,
    hasUnsavedChanges,
    error,
    snackbarOpen,
    codeOptions,

    // Setters
    setDefinitions,

    // Redux actions
    openEditModal: (def: CustomDefinition | null) => dispatch(openEditModal(def)),
    closeEditModal: () => dispatch(closeEditModal()),
    openTestModal: (metric: CustomDefinition) => dispatch(openTestModal(metric)),
    closeTestModal: () => dispatch(closeTestModal()),
    setTemplateTab: (tab: string) => dispatch(setTemplateTab(tab)),
    clearError: () => dispatch(clearError()),
    setSnackbarOpen: (open: boolean) => dispatch(setSnackbarOpen(open)),

    // Actions
    handleSave,
    confirmSave,
    addNewDefinition,
    handleDelete,
    handleEditSave,
    handleUndoChanges,
    handleCloseConfirmDialog,
    handleTestFromEdit,
    handleEditFromTest
  }
} 